/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { TextField } from '@mui/material';
import type { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';

export function decimalToDegreesMinutes(value: number): string {
  const sign = value < 0 ? '-' : '';
  let abs = Math.abs(value);

  const degrees = Math.floor(abs);
  const minutes = Math.round((abs - degrees) * 60);

  // normalize rounding overflow (e.g. 59.9 min → +1 degree)
  if (minutes === 60) {
    abs += 1;
    return decimalToDegreesMinutes(sign === '-' ? -abs : abs);
  }

  return `${sign}${degrees}°${minutes.toString().padStart(2, '0')}′`;
}

export function degreesMinutesToDecimal(input: string | number): number {
  const raw = input.toString().trim();

  const negative = raw.startsWith('-');
  const digits = raw.replace(/[^0-9]/g, '');
  const sign = negative ? -1 : 1;

  if (digits.length <= 2) {
    const deg = parseInt(digits || '0', 10);
    return sign * deg;
  }

  // split into degrees + minutes
  const deg = parseInt(digits.slice(0, digits.length - 2), 10);
  let min = parseInt(digits.slice(-2), 10);

  // normalize minutes overflow (e.g. 65 → 1°05′)
  const extraDeg = Math.floor(min / 60);
  min %= 60;

  return sign * (deg + extraDeg + min / 60);
}

interface Props {
  name: string;
  label: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  size?: 'small' | 'medium';
  isLongitude?: boolean;
  t: TFunction;
}

export const DegreesMinutesField: React.FC<Props> = ({
  name,
  label,
  disabled = false,
  isReadOnly = false,
  size = 'small',
  isLongitude = false,
  t,
}) => {
  const { control } = useFormContext();
  const [localInput, setLocalInput] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { value, onChange } = field;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
          setLocalInput(e.target.value);
          setIsEditing(true);
        };

        const handleBlur = (): void => {
          if (localInput != null && localInput !== '-' && localInput !== '') {
            const input = localInput.replace(/[^\d-]/g, '');
            const negative = input.startsWith('-');
            let digits = input.replace(/[^0-9]/g, '');

            // Latitude: 4 digits (DDMM), Longitude: 5 (DDDMM)
            const maxLen = isLongitude ? 5 : 4;
            if (digits.length > maxLen) {
              digits = digits.slice(0, maxLen);
            }

            const normalized = negative ? `-${digits}` : digits;

            try {
              const decimal = degreesMinutesToDecimal(normalized);
              onChange(decimal);
            } catch {
              // Invalid, do not update form value
            }
          }
          setIsEditing(false);
          setLocalInput(null);
        };

        let displayValue: string;
        if (isEditing) {
          displayValue = localInput ?? '';
        } else if (value != null) {
          displayValue = decimalToDegreesMinutes(value);
        } else {
          displayValue = '';
        }

        return (
          <TextField
            disabled={disabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            label={label}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={isLongitude ? 'DDD°MM′' : 'DD°MM′'}
            size={size}
            slotProps={{
              input: {
                readOnly: isReadOnly,
              },
            }}
            value={displayValue}
          />
        );
      }}
      rules={{
        validate: (value: number) => {
          if (value == null || isNaN(value)) {
            return true;
          } // let empty pass
          if (!isLongitude && (value < -90 || value > 90)) {
            return t('form-fields-error-latitude-minutes');
          }
          if (isLongitude && (value < -180 || value > 180)) {
            return t('form-fields-error-longitude-minutes');
          }
          return true;
        },
      }}
    />
  );
};
